// =============================================
// NotesTab — the YKV workhorse: search + category filter + entries ⇄ editor
// =============================================
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiPlus, FiSearch, FiStar, FiTrash2, FiBell } from 'react-icons/fi';
import {
  NoteRow, CategoryRow, LinkRow, NotesBundle,
  createNote, updateNote, deleteNote, categoryBreadcrumb,
  parseList, isReviewDue,
} from './notesLib';
import NoteEntryEditor from './NoteEntryEditor';
import CategoryTree from './CategoryTree';

interface Props {
  notes: NoteRow[];
  categories: CategoryRow[];
  links: LinkRow[];
  aiEnabled: boolean;
  onReload: () => Promise<NotesBundle>;
  openNoteId?: number | null;
  openCategoryId?: number | null;
}

function preview(n: NoteRow): string {
  const t = (n.key_takeaway || n.content_text || '').trim();
  return t.length > 150 ? `${t.slice(0, 150)}…` : t || 'Empty entry';
}

const NotesTab: React.FC<Props> = ({ notes, categories, links, aiEnabled, onReload, openNoteId, openCategoryId }) => {
  const [view, setView] = useState<'list' | 'edit'>('list');
  const [activeId, setActiveId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState<number | null>(null);
  const now = useMemo(() => new Date(), []);

  useEffect(() => { if (openNoteId) { setActiveId(openNoteId); setView('edit'); } }, [openNoteId]);
  useEffect(() => { if (openCategoryId != null) { setCatFilter(openCategoryId); setView('list'); } }, [openCategoryId]);

  const catsByNote = useMemo(() => {
    const m = new Map<number, Set<number>>();
    links.forEach(l => { if (l.is_deleted == 1) return; if (!m.has(Number(l.note_id))) m.set(Number(l.note_id), new Set()); m.get(Number(l.note_id))!.add(Number(l.category_id)); }); // eslint-disable-line eqeqeq
    return m;
  }, [links]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return notes.filter(n => {
      if (catFilter != null && !(catsByNote.get(Number(n.note_id))?.has(catFilter))) return false;
      if (!q) return true;
      return [n.title, n.content_text, n.tags, n.keywords, n.key_takeaway].some(f => (f || '').toLowerCase().includes(q));
    }).sort((a, b) => (Number(b.is_pinned) || 0) - (Number(a.is_pinned) || 0) || (b.updated_at || '').localeCompare(a.updated_at || ''));
  }, [notes, search, catFilter, catsByNote]);

  const activeNote = useMemo(() => notes.find(n => n.note_id === activeId) || null, [notes, activeId]);

  const openNote = (id: number) => { setActiveId(id); setView('edit'); };
  const newEntry = useCallback(async () => {
    const n = await createNote({ title: '' });
    if (catFilter != null) { /* link to current filter after reload below */ }
    await onReload(); openNote(n.note_id);
  }, [onReload, catFilter]);

  const back = useCallback(async () => { setView('list'); setActiveId(null); await onReload(); }, [onReload]);
  const toggleImportant = useCallback(async (n: NoteRow) => { await updateNote(n.note_id, { is_important: n.is_important == 1 ? 0 : 1 }); await onReload(); }, [onReload]); // eslint-disable-line eqeqeq
  const removeNote = useCallback(async (n: NoteRow) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${n.title || 'Untitled'}"?`)) return;
    await deleteNote(n.note_id);
    if (activeId === n.note_id) { setView('list'); setActiveId(null); }
    await onReload();
  }, [onReload, activeId]);

  if (view === 'edit' && activeNote) {
    return (
      <NoteEntryEditor
        key={activeNote.note_id}
        note={activeNote}
        categories={categories}
        links={links}
        aiEnabled={aiEnabled}
        onBack={back}
        onDelete={() => removeNote(activeNote)}
        onReload={onReload}
      />
    );
  }

  const activeCatLabel = catFilter != null
    ? categoryBreadcrumb(categories.find(c => c.category_id == catFilter)!, categories) // eslint-disable-line eqeqeq
    : null;

  return (
    <div className="notes-body">
      <aside className="notes-cats">
        <h3 className="notes-section-title">Categories</h3>
        <button className={`notes-cat-row ${catFilter == null ? 'active' : ''}`} onClick={() => setCatFilter(null)}>{/* eslint-disable-line eqeqeq */}
          <span className="ncr-name">All entries</span><span className="ncr-count">{notes.length}</span>
        </button>
        <CategoryTree
          categories={categories}
          links={links}
          onReload={onReload}
          compact
          selectedId={catFilter}
          onSelect={setCatFilter}
        />
      </aside>

      <section className="notes-list-wrap">
        <div className="notes-list-head">
          <div className="notes-search">
            <FiSearch size={15} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search entries — title, text, tags, keywords…" />
          </div>
          {activeCatLabel && <span className="notes-filter-chip" onClick={() => setCatFilter(null)}>{activeCatLabel} ✕</span>}
          <button className="notes-new-btn" onClick={() => void newEntry()}><FiPlus size={16} /> New entry</button>
        </div>

        {filtered.length === 0 ? (
          <div className="notes-empty">
            {notes.length === 0 ? <>No entries yet. <button className="notes-link-btn" onClick={() => void newEntry()}>Create your first one →</button></> : 'No entries match your search.'}
          </div>
        ) : (
          <div className="notes-grid">
            {filtered.map(n => {
              const tags = parseList(n.tags);
              const due = isReviewDue(n, now);
              return (
                <div key={n.note_id} className={`notes-card ${n.is_important == 1 ? 'important' : ''}`}>{/* eslint-disable-line eqeqeq */}
                  <button className="notes-card-main" onClick={() => openNote(n.note_id)}>
                    <div className="nc-title">
                      {n.is_pinned == 1 && <span className="nc-pin">📌</span>}{/* eslint-disable-line eqeqeq */}
                      {n.title || 'Untitled'}
                      {due && <span className="nc-due" title="Due for review"><FiBell size={12} /></span>}
                    </div>
                    <div className="nc-prev">{preview(n)}</div>
                    {tags.length > 0 && <div className="nc-tags">{tags.slice(0, 5).map(t => <span key={t} className="nc-tag">{t}</span>)}</div>}
                    <div className="nc-meta">
                      {(catsByNote.get(Number(n.note_id))?.size || 0) > 0 && <span className="nc-cat-pill">{catsByNote.get(Number(n.note_id))!.size} cat</span>}
                    </div>
                  </button>
                  <div className="notes-card-actions">
                    <button title="Important" className={n.is_important == 1 ? 'on' : ''} onClick={() => toggleImportant(n)}><FiStar size={14} /></button>{/* eslint-disable-line eqeqeq */}
                    <button title="Delete" onClick={() => removeNote(n)}><FiTrash2 size={14} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default NotesTab;
